// src/utils/createFormValues.ts
import CustodianDetailsViewModel from '../Models/custodian/custodian-details-viewmodel';
import { DEFAULT_SERVE_PREFERENCES, DEFAULT_BILLING_INFO } from './formDefaults';

const toIds = (arr: any[] = []) =>
  arr.map(item =>
    typeof item === 'object'
      ? item?.recordTypeId ?? item?.facilityTypeId ?? item?.id ?? item
      : item,
  );

const mergeBilling = (source: any) => {
  if (!source) return DEFAULT_BILLING_INFO;
  const { id, billingInfoInvoicingPaymentMethods, billingInfoProcessingFeesMethods, ...rest } = source;
  return {
    id,
    ...DEFAULT_BILLING_INFO,
    ...rest,
    invoicePaymentMethods: billingInfoInvoicingPaymentMethods ?? [],
    processingFeesMethods: billingInfoProcessingFeesMethods ?? [],
  };
};

export const createFormValues = (data: CustodianDetailsViewModel) => {
  const cd = data?.custodianDetails;
  const billing = cd?.billingInformation;

  return {
    id: data?.id,
    name: data?.name,
    isFacility: data?.isFacility,
    isCustodian: data?.isCustodian,
    notes: data?.notes,
    disabled: data?.disabled ?? false,

    facilityTypes: toIds(data?.facilityTypes),
    facilityTypeNotes: data?.facilityTypeNotes,

    addressInfo: {
      addressDetails: data?.address?.addressDetails ?? null,
      city: data?.address?.city ?? null,
      stateId: data?.address?.stateId ?? null,
      postCode: data?.address?.postCode ?? null,
      timeZoneId: data?.address?.timeZoneId ?? null,
    },

    mailingAddress: {
      addressDetails: data?.mailingAddress?.addressDetails ?? '',
      city: data?.mailingAddress?.city ?? '',
      stateId: data?.mailingAddress?.stateId ?? '',
      postCode: data?.mailingAddress?.postCode ?? '',
      timeZoneId: data?.mailingAddress?.timeZoneId ?? null,
    },

    emails: data?.emailAddresses?.length
      ? data.emailAddresses.map(e => ({ id: e.id, email: e.email }))
      : [{ email: '', hasDuplicateError: false }],

    portalUrls: data?.portalUrls?.length
      ? data.portalUrls.map(p => ({
          portalUrl: p.portalUrl,
          portalTypeId: p.portalTypeId,
          portalNotes: p.portalNotes,
        }))
      : [{ portalUrl: '', portalTypeId: null, portalNotes: null, hasDuplicateError: false }],

    externalSourceMappings: data?.externalSourceMappings?.length
      ? data.externalSourceMappings.map(m => ({
          mappedId: m.mappedId,
          sourceTypeId: m.sourceTypeId,
          notes: m.notes,
        }))
      : [{ mappedId: '', sourceTypeId: null, notes: null }],

    phones: data?.phoneNumbers?.map(p => ({
      id: p.id,
      phoneNumber: p.phoneNumber,
      phoneTypeId: p.phoneTypeId,
      isPrimary: p.isPrimary ?? false,
    })) ?? [],

    contacts: data?.contacts?.length
      ? data.contacts.map(c => ({
          name: c.name,
          phoneNumber: c.phoneNumber,
          email: c.email,
          title: c.title,
          isPrimary: c.isPrimary ?? false,
        }))
      : [{ isPrimary: false, email: '', phoneNumber: '', name: '', title: '' }],

    departmentAndPhysicians: data?.departmentAndPhysicians?.length
      ? data.departmentAndPhysicians.map(dp => ({
          department: dp.department,
          name: dp.name,
          phoneNumber: dp.phoneNumber,
          additionalInformation: dp.additionalInformation ?? '',
        }))
      : [{ department: null, name: null, phoneNumber: null, additionalInformation: null }],

    custodianDetails: {
      recordTypes: toIds(cd?.recordTypes),
      recordTypeNotes: cd?.recordTypeNotes,
      orderTypeName: cd?.orderTypeName,
      orderTypeId: cd?.orderTypeId,
      orderTypeNotes: cd?.orderTypeNotes,

      daysAvailable: cd?.daysAvailable?.map(day => ({
        dayOfWeek: day.dayId ?? day.dayOfWeek,
        timeZoneId: day.timeZoneId,
        serveHours: { from: day.serveHours?.from, to: day.serveHours?.to },
        pickupAndCopyHours: { from: day.pickupAndCopyHours?.from, to: day.pickupAndCopyHours?.to },
        bestTimeToCall: { from: day.bestTimeToCall?.from, to: day.bestTimeToCall?.to },
      })) ?? [],

      serveMethods: cd?.serveMethods?.map(m => ({ serveMethodId: m.serveMethodId })) ?? [],
      roiMethods: cd?.roiMethods?.map(m => ({ serveMethodId: m.serveMethodId })) ?? [],
      serveMethodNotes: cd?.serveMethodNotes ?? '',
      roiMethodNotes: cd?.roiMethodNotes ?? '',
      followUpMethods: cd?.followUpMethods?.map(f => f.followUpMethodId) ?? [],
      entityMetric: cd?.entityMetric ?? { averageProcessingTime: null, lastRecordRetrievedDate: null },

      servePreferences: {
        // Always use defaults as base
        ...DEFAULT_SERVE_PREFERENCES,
        // Apply any user preferences (if they exist)
        ...(cd?.servePreferences),
        // ALWAYS include files from custodianDetails.files — never lose them!
        files: cd?.files?.length
          ? cd.files.map(f => ({
              id: f.id,
              fileName: f.fileName,
              fileTypeId: f.fileTypeId,
              permanentBlobUrl: f.permanentBlobUrl,
              fileTypeName: f.fileTypeName,
              fileSize: f.fileSize,
            }))
          : [],
      },

      billingInformation: mergeBilling(billing),
    },
  };
};